/**
 * This component will render Event Details with an action button to perform RSVP / Cancel RSVP to event.
 *
 * @module views/components/EventDetailCard
 * @memberof -Common
 */
import './EventDetailCard.scss';

import React, { useMemo, useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import { constants } from '@ulta/utils/constants/constants';

import * as utils from './EventDetailCard';

/**
 * Represents a EventDetailCard component
 *
 * @method
 * @param {EventDetailCard} props - React properties passed from composition
 * @returns EventDetailCard
 */
export const EventDetailCard = function( props ){
  const {
    body1,
    body2,
    body3,
    eventActionsGroup,
    eyebrow,
    getDirectionLink,
    image,
    invokeAction,
    invokeMutation,
    loading,
    maxWidthAspectRatio,
    root,
    rootMargin,
    style = defaultProps.style,
    threshold,
    title1,
    title2,
    title3
  } = props;
  const eventDetailsCardRef = useRef( null );

  const { meta }  = eventActionsGroup || {};

  useIntersectionObserver( eventDetailsCardRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: threshold
  }, handleIntersection( props ) );
  if( !image && !eyebrow && !title1 && !body1 && !body2 && !body3 ){
    return null;
  }

  const aspectRatio = useMemo( () => utils.getAspectRatio( { maxWidthAspectRatio } ), [maxWidthAspectRatio] );

  return (
    <div className={ classNames( 'EventDetailCard', {
      [`EventDetailCard__style-${style}`]: style
    } ) }
    ref={ eventDetailsCardRef }
    >
      <GridContainer>
        <div className='EventDetailCard__contentImage' >
          <UltaAsset { ...image }
            { ...aspectRatio }
          />
        </div>
        <div className='EventDetailCard__contentDetails'>
          { eyebrow && (
            <div className='EventDetailCard__eyebrow'>
              <Text
                htmlTag='p'
                textStyle='eyebrow'
              >
                { eyebrow }
              </Text>
            </div>
          ) }
          { title1 && (
            <div className='EventDetailCard__title1'>
              <Text
                htmlTag='h1'
                textStyle='title-xlg'
              >
                { title1 }
              </Text>
            </div>
          ) }
          { body1 && (
            <div className='EventDetailCard__body1'>
              <Text
                htmlTag='p'
                textStyle='body-2'
              >
                { body1 }
              </Text>
            </div>
          ) }
          { body2 && (
            <div className='EventDetailCard__title2'>
              <Text
                htmlTag='h2'
                textStyle='title'
              >
                { title2 }
              </Text>
              <div className='EventDetailCard__addressContainer'>
                <Text
                  htmlTag='p'
                  textStyle='body'
                >
                  { body2 }
                </Text>
                { getDirectionLink?.label &&
                  <div className='EventDetailCard__directionLink'>
                    <Link_Huge
                      target='_blank'
                      url={ getDirectionLink.url }
                      action={ getDirectionLink }
                      withHover
                      iconName='arrowUpForward'
                      iconPosition='after'
                      screenReaderText={ getDirectionLink.accessibility }
                      size='compact'
                    >
                      { getDirectionLink.label }
                    </Link_Huge>
                  </div>
                }
              </div>
            </div>
          ) }
          { title3 && (
            <div className='EventDetailCard__title3'>
              <Text
                htmlTag='h2'
                textStyle='title'
              >
                { title3 }
              </Text>
            </div>
          ) }
          { body3 && (
            <div className='EventDetailCard__body3'>
              <Text
                htmlTag='p'
                textStyle='body'
              >
                { body3 }
              </Text>
            </div>
          ) }
          { eventActionsGroup && (
            <AsyncComponent
              { ...eventActionsGroup }
              moduleName='EventActions'
              meta={ meta }
            />
          ) }
        </div>
      </GridContainer>
    </div>
  );
};


/**
 * getAspectRatio method will return aspect ratio UltaAsset as string
 * @param { object } data : properties in object format
 * @returns aspectRatio
 */

export const getAspectRatio = ( data ) => {
  const { maxWidthAspectRatio } = data || {};
  return `${Number( maxWidthAspectRatio ) || 1}:1`;
};

/**
 * Property type definitions
 * @typedef EventDetailCardProps
 * @type {object}
 * @property {string} body1 - The first body text of the component.
 * @property {string} body2 - The second body text of the component.
 * @property {string} body3 - The third body text of the component.
 * @property {EventActionsGroupPropTypes} eventActionsGroup - The event actions group for the component.
 * @property {string} eyebrow - The eyebrow text (typically a subtitle or introductory text).
 * @property {ImagePropTypes} image - The image to be displayed.
 * @property {function} invokeAction - invokes graphql action found in EventDetailCard Actions
 * @property {function} invokeMutation - invokes graphql calls found in EventDetailCard Actions
 * @property {string} [loading] - The loading state of the component (optional).
 * @property {string} [root] - The root element for the intersection observer (optional).
 * @property {string} rootMargin - The root margin for the component's visibility in the viewport.
 * @property {string} style - The style of the card ('offset', '50-50', 'inset-50-50').
 * @property {Array<number>} threshold - The threshold values for the intersection observer.
 * @property {string} title1 - The main title of the component.
 * @property {string} title2 - The second title of the component.
 * @property {string} title3 - The third title of the component.
 *
 */

export const propTypes = {
  body1: PropTypes.string,
  body2: PropTypes.string,
  body3: PropTypes.string,
  eventActionsGroup: PropTypes.object,
  eyebrow: PropTypes.string,
  image: PropTypes.object,
  invokeMutation: PropTypes.func,
  loading: PropTypes.string,
  root: PropTypes.string,
  rootMargin: PropTypes.string,
  style: PropTypes.oneOf( ['offset', '50-50'] ), // applies exclusively to LG and XL size
  threshold: PropTypes.arrayOf( PropTypes.number ),
  title1: PropTypes.string,
  title2: PropTypes.string,
  title3: PropTypes.string
};

/**
  * Default values for passed properties
  * @type {object}
  */
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS,
  style: 'offset'
};

EventDetailCard.propTypes = propTypes;
EventDetailCard.defaultProps = defaultProps;
export default EventDetailCard;
